import {
  COUNTRIES_BY_CODE,
  EUROPEAN_UNION_CODE,
  EUROPEAN_UNION_LABEL,
  areAllEuCountriesSelected,
  toggleEuCountries,
} from "@redotech/locale/countries";
import { COUNTRY_CODES } from "@redotech/locale/country-codes";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import type { CustomerCountryState } from "@redotech/redo-return-core-common/return-flow/condition-validate";
import { ConditionTypeDetailsProps } from "@redotech/redo-return-ui/flows/return-flow/condition";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { memo, useMemo, useState } from "react";

export const CustomerCountryDetails = memo(function CustomerCountryDetails({
  state,
  setState,
  disabled,
}: ConditionTypeDetailsProps<CustomerCountryState>) {
  const { t } = useTranslation();
  const [searchString, setSearchString] = useState("");

  const euOption: BadgeRedoListItem<string> = useMemo(
    () => ({
      id: EUROPEAN_UNION_CODE,
      value: EUROPEAN_UNION_CODE,
      type: "badge" as const,
      badge: { text: EUROPEAN_UNION_LABEL, color: "gray" as const },
    }),
    [],
  );

  const countryOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    return [
      euOption,
      ...COUNTRY_CODES.map((countryCode) => ({
        id: countryCode,
        value: countryCode,
        type: "badge" as const,
        badge: {
          text: COUNTRIES_BY_CODE[countryCode] || countryCode,
          color: "gray" as const,
        },
      })),
    ];
  }, [euOption]);

  const filteredOptions = useMemo(() => {
    if (!searchString) {
      return countryOptions;
    }
    const searchLower = searchString.toLowerCase();
    return countryOptions.filter((option) => {
      const text = option.badge.text;
      const id = option.id;
      return (
        (typeof text === "string" &&
          text.toLowerCase().includes(searchLower)) ||
        (typeof id === "string" && id.toLowerCase().includes(searchLower))
      );
    });
  }, [countryOptions, searchString]);

  const isAllEuSelected = useMemo(
    () => areAllEuCountriesSelected(state),
    [state],
  );

  const selectedCountries: BadgeRedoListItem<string>[] = useMemo(() => {
    const badges: BadgeRedoListItem<string>[] = [];
    if (isAllEuSelected) {
      badges.push(euOption);
    }
    for (const countryCode of state) {
      badges.push({
        id: countryCode,
        value: countryCode,
        type: "badge" as const,
        badge: {
          text: COUNTRIES_BY_CODE[countryCode] || countryCode,
          color: "gray" as const,
        },
      });
    }
    return badges;
  }, [isAllEuSelected, euOption, state]);

  const handleSelectionChange = useHandler(
    (items: BadgeRedoListItem<string>[]) => {
      const wasAllEuSelected = areAllEuCountriesSelected(state);
      const hasEuInNewItems = items.some(
        (item) => item.value === EUROPEAN_UNION_CODE,
      );

      if (!wasAllEuSelected && hasEuInNewItems) {
        setState(toggleEuCountries(state, true) as CustomerCountryState);
      } else if (wasAllEuSelected && !hasEuInNewItems) {
        setState(toggleEuCountries(state, false) as CustomerCountryState);
      } else {
        const updated = items
          .filter((item) => item.value !== EUROPEAN_UNION_CODE)
          .map((item) => item.value);
        setState(updated as CustomerCountryState);
      }
    },
  );

  return (
    <RedoTagsDropdownInput
      disabled={disabled}
      label={t("Countries")}
      options={filteredOptions}
      placeholder={t("Select countries...")}
      searchPlaceholder={t("Search countries...")}
      searchString={searchString}
      selectedOptions={selectedCountries}
      setSearchString={setSearchString}
      setSelectedOptions={handleSelectionChange}
      size="xs"
    />
  );
});
